import {useRef, useState, type ReactNode} from 'react'
import {TableIcon, TriangleDownIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Link} from '@primer/react'
import {SkeletonText, Stack} from '@primer/react/experimental'

import type {
  PullRequestProject,
  PullRequestProjectStatus,
} from '@github-ui/dotcom-schema/types/pull_requests/v1/projects'
import {useUpdateProjectStatus} from '../../mutations/use-update-project-status'
import {useProjectsQuery} from './data/route-queries'
import {MutationFailureBanner} from '../FailureBanner'
import {ProjectFieldsList, ProjectFieldsToggle} from './ProjectFields'
import sidebarListStyles from './SidebarActionList.module.css'
import styles from './Projects.module.css'

export function Projects() {
  const {data, isPending, isError} = useProjectsQuery()
  if (isPending) return <SkeletonProjects />
  if (isError) return <span className={sidebarListStyles.emptyState}>Unable to load projects</span>

  const projects = data?.projects
  if (!projects || projects.length === 0) {
    return <span className={sidebarListStyles.emptyState}>None yet</span>
  }

  return (
    <ul className={styles.list}>
      {[...projects].reverse().map(project => (
        <ProjectRow key={project.id} project={project} />
      ))}
    </ul>
  )
}

// Renders each project as a bordered card -- a title row (link + closed badge), the project's
// "Status" field (with the "+N more" additional-fields toggle at the end of that same line, when
// present), and -- when expanded -- the additional fields below -- mirroring the Rails sidebar's
// `issues/sidebar/_memex_project_item` partial + `Sidebar::MemexStatusComponent`.
function ProjectRow({project}: {project: PullRequestProject}) {
  const [fieldsExpanded, setFieldsExpanded] = useState(false)
  const hasAdditionalFields = project.fields.length > 0

  const fieldsToggle = hasAdditionalFields ? (
    <ProjectFieldsToggle
      fieldCount={project.fields.length}
      expanded={fieldsExpanded}
      onToggle={() => setFieldsExpanded(expanded => !expanded)}
      className={project.status ? styles.fieldsToggleInline : undefined}
    />
  ) : null

  return (
    <li className={styles.card}>
      <div className={styles.header}>
        <Link href={project.url} className={styles.titleLink} muted>
          <TableIcon className={styles.titleIcon} />
          {project.title}
        </Link>
        {project.closed && <span className={styles.closedBadge}>Closed</span>}
      </div>
      {project.status && <ProjectStatusField project={project} status={project.status} fieldsToggle={fieldsToggle} />}
      {!project.status && fieldsToggle}
      {hasAdditionalFields && fieldsExpanded && <ProjectFieldsList project={project} />}
    </li>
  )
}

function ProjectStatusField({
  project,
  status,
  fieldsToggle,
}: {
  project: PullRequestProject
  status: PullRequestProjectStatus
  fieldsToggle: ReactNode
}) {
  const updateStatusMutation = useUpdateProjectStatus()
  const {mutate, isPending} = updateStatusMutation
  const statusButtonRef = useRef<HTMLButtonElement>(null)
  const currentOption = status.options.find(option => option.id === status.optionId)
  const label = currentOption?.name ?? 'No status'

  if (!project.viewerCanUpdate) {
    return (
      <div className={styles.statusRow}>
        <div className={styles.statusRowLine}>
          <span className={styles.statusText}>Status: {label}</span>
          {fieldsToggle}
        </div>
      </div>
    )
  }

  return (
    <div className={styles.statusRow}>
      <div className={styles.statusRowLine}>
        <ActionMenu>
          <ActionMenu.Button
            ref={statusButtonRef}
            variant="invisible"
            size="small"
            trailingAction={TriangleDownIcon}
            className={styles.statusButton}
            aria-label={`Status: ${label} for ${project.title}`}
            inactive={isPending || undefined}
          >
            Status: {label}
          </ActionMenu.Button>
          <ActionMenu.Overlay align="start">
            <ActionList selectionVariant="single">
              {status.options.map(option => (
                <ActionList.Item
                  key={option.id}
                  selected={option.id === status.optionId}
                  onSelect={() => {
                    if (isPending || option.id === status.optionId) return
                    mutate({
                      projectId: project.id,
                      itemId: project.itemId,
                      fieldId: status.fieldId,
                      optionId: option.id,
                    })
                  }}
                >
                  {option.name}
                </ActionList.Item>
              ))}
            </ActionList>
          </ActionMenu.Overlay>
        </ActionMenu>
        {fieldsToggle}
      </div>
      <MutationFailureBanner
        mutation={updateStatusMutation}
        title="Failed to update project status"
        returnFocusRef={statusButtonRef}
        className={styles.mutationError}
      />
    </div>
  )
}

function SkeletonProjects() {
  return (
    <Stack direction="vertical" gap="condensed">
      <span className="sr-only">Loading...</span>
      <SkeletonText width="10ch" />
      <SkeletonText width="7ch" />
    </Stack>
  )
}
